/**
 * Gate refusal copy for the DD download (inc5 step E, customer-loop scope
 * §2/§4). The server (downloadDataDictionaryV2) returns {blocked: true,
 * verification: {...}} instead of a CSV when the gate refuses; this maps
 * that payload to the customer-facing line. Pure and React-free — tested
 * under the functions package's vitest (`npm run test:web`).
 *
 * Copy rules: plain language, no raw statuses or check ids, never "AI".
 */

export interface DdDownloadResponseLike {
  blocked?: boolean;
  csv?: string;
  verification?: {
    status?: string;
    items?: Array<{ subject?: string; data?: Record<string, string> } | unknown>;
    staleReasons?: string[];
  };
}

/**
 * How many open items a refusal stands for. The run record keeps only the
 * first 100 S3 items; a single "beyond the recorded items" entry carries the
 * rest in data.count, so the number shown is the true total, not the length of
 * the list.
 */
export function openItemCount(items: unknown[] | undefined): number {
  let n = 0;
  for (const raw of items ?? []) {
    const it = (raw ?? {}) as { subject?: string; data?: Record<string, string> };
    const beyond = typeof it.subject === 'string' && /^S3:.*:beyond-recorded-items$/.test(it.subject);
    const extra = beyond ? Number(it.data?.count) : NaN;
    n += Number.isFinite(extra) && extra > 0 ? extra : 1;
  }
  return n;
}

/** Generic refusal line for any gated deliverable (inc5 step G). */
export function gateRefusalMessage(docLabel: string, data: DdDownloadResponseLike): string | null {
  if (!data?.blocked) return null;
  if (data.verification?.status === 'action-needed') {
    const n = openItemCount(data.verification.items);
    return n === 1
      ? `The ${docLabel} has 1 open item on the Verification tab. Resolve it to download.`
      : `The ${docLabel} has ${n} open items on the Verification tab. Resolve them to download.`;
  }
  // stale-or-missing after the inline re-run: a concurrent change, not an error.
  return 'The study changed while the document was being verified. Try the download again.';
}

/** The toast line for a refused DD download; null when nothing is blocked. */
export function ddGateRefusalMessage(data: DdDownloadResponseLike): string | null {
  if (!data?.blocked) return null;
  if (data.verification?.status === 'action-needed') {
    const n = openItemCount(data.verification.items);
    return n === 1
      ? 'The data dictionary has 1 open item on the Verification tab. Resolve it to download.'
      : `The data dictionary has ${n} open items on the Verification tab. Resolve them to download.`;
  }
  // stale-or-missing after the inline re-run: a concurrent change, not an error.
  return 'The study changed while the dictionary was being verified. Try the download again.';
}
